import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
} from "@mui/material";
import { posts, locations as locationsApi } from "../api/endpoints";
import type { PostType } from "../types";
import { useLanguage } from "../context/LanguageContext";
import { getBrowserPosition, reverseGeocode } from "../utils/locationUtils";

interface CreatePostProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const POST_TYPES: { value: PostType; labelKey: string }[] = [
  { value: "general_discussion", labelKey: "feed.generalDiscussion" },
  { value: "job_offer", labelKey: "feed.jobOffer" },
  { value: "job_request", labelKey: "feed.jobRequest" },
  { value: "housing_offer", labelKey: "feed.housingOffer" },
  { value: "housing_request", labelKey: "feed.housingRequest" },
  { value: "friend_request", labelKey: "feed.friendRequest" },
  { value: "other", labelKey: "feed.other" },
];

const PLACEHOLDERS: Record<PostType, string> = {
  general_discussion: "Share something with the community...",
  job_offer: "Describe the job title, requirements, pay, and how to apply...",
  job_request: "Describe the kind of work you're looking for...",
  housing_offer: "Describe the property, rent, location, and availability...",
  housing_request: "Describe what kind of housing you're looking for...",
  friend_request: "Tell people a bit about yourself and what you're looking for...",
  other: "What's on your mind?",
};

export default function CreatePost({ open, onClose, onCreated }: CreatePostProps) {
  const { t } = useLanguage();
  const [postType, setPostType] = useState<PostType>("general_discussion");
  const [content, setContent] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Each post gets its own fresh location row — we never share user_settings.location_id
  // here because that row represents where the user lives, not where a specific post was
  // written. If GPS is unavailable we leave location_id null; the backend's
  // "OR p.location_id IS NULL" clause still shows the post in the author's country feed.
  const [postLocationId, setPostLocationId] = useState<number | null>(null);
  const gpsAttemptedRef = useRef(false);

  useEffect(() => {
    if (!open || gpsAttemptedRef.current) return;
    gpsAttemptedRef.current = true;

    (async () => {
      try {
        const pos = await getBrowserPosition();
        const geo = await reverseGeocode(pos.latitude, pos.longitude);
        if (geo.countryCode) {
          const loc = await locationsApi.create({
            address_line1: geo.city ?? geo.countryName,
            city: geo.city ?? null,
            state_province: geo.stateProvince ?? null,
            country_code: geo.countryCode,
            latitude: pos.latitude,
            longitude: pos.longitude,
          });
          if (loc?.id) setPostLocationId(loc.id);
        }
      } catch {
        // GPS denied or failed — post will be submitted without a location_id.
        // Country-based filtering still works via the author's user_settings.
      }
    })();
  }, [open]);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await posts.create({
        content: content.trim(),
        post_type: postType,
        is_anonymous: isAnonymous,
        location_id: postLocationId, // null when GPS was unavailable — intentional
      });
      setContent("");
      setPostType("general_discussion");
      setIsAnonymous(false);
      setPostLocationId(null);
      gpsAttemptedRef.current = false;
      onCreated?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create post");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setContent("");
      setError(null);
      setPostLocationId(null);
      gpsAttemptedRef.current = false;
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("feed.createPost")}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
          <InputLabel id="post-type-label">{t("feed.postType")}</InputLabel>
          <Select
            labelId="post-type-label"
            value={postType}
            label={t("feed.postType")}
            onChange={(e) => setPostType(e.target.value as PostType)}
          >
            {POST_TYPES.map((pt) => (
              <MenuItem key={pt.value} value={pt.value}>
                {t(pt.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          multiline
          rows={5}
          fullWidth
          placeholder={PLACEHOLDERS[postType]}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          variant="outlined"
        />
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                size="small"
              />
            }
            label={t("feed.postAnonymously")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !content.trim()}
          sx={{
            color: "white",
            "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", bgcolor: "rgba(0,0,0,0.25)" },
          }}
        >
          {loading ? t("common.posting") : t("common.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
